import {
    getInterpolatedLineLayerWidth,
    getInterpolatedSelectableLineLayerWidth,
    getLineLayerConfig,
    getSelectableLineSortKey,
    INTERPOLATION_ZOOM_MAX,
    MAX_LINE_OFFSET,
    MIN_LINE_OFFSET,
    SELECTED_LINE_LAYER_SIZE,
} from "@l2r-front/l2r-map"
import { selectLayerColorOpacity } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../common/constants/i18n"

import { SPEEDINGS_HEAVY_VEHICLE_ACTIVE_LAYER, SPEEDINGS_LIGHT_VEHICLE_ACTIVE_LAYER } from "../constants/urlParams"

export const trafficLegend = {
    [SPEEDINGS_HEAVY_VEHICLE_ACTIVE_LAYER]: [
        {
            minValue: 500,
            color: "_components/legend/DPE/rouge",
            label: "containers.heavyTraffic.legend.scale.veryHigh",
        },
        {
            minValue: 50,
            maxValue: 500,
            color: "_components/legend/DPE/orange",
            label: "containers.heavyTraffic.legend.scale.high",
        },
        {
            minValue: 5,
            maxValue: 50,
            color: "_components/legend/DPE/jaune",
            label: "containers.heavyTraffic.legend.scale.medium",
        },
        {
            minValue: 5,
            maxValue: 0,
            color: "_components/legend/DPE/vert-foncé",
            label: "containers.heavyTraffic.legend.scale.low",
        },
    ],
    [SPEEDINGS_LIGHT_VEHICLE_ACTIVE_LAYER]: [
        {
            minValue: 10000,
            color: "_components/legend/DPE/rouge",
            label: "containers.lightTraffic.legend.scale.veryHigh",
        },
        {
            minValue: 1000,
            maxValue: 10000,
            color: "_components/legend/DPE/orange",
            label: "containers.lightTraffic.legend.scale.high",
        },
        {
            minValue: 100,
            maxValue: 1000,
            color: "_components/legend/DPE/jaune",
            label: "containers.lightTraffic.legend.scale.medium",
        },
        {
            minValue: 0,
            maxValue: 100,
            color: "_components/legend/DPE/vert-foncé",
            label: "containers.lightTraffic.legend.scale.low",
        },
    ],
}

export const getTrafficLegendConfig = (legendKey, t, theme) => {
    return trafficLegend[legendKey].map(legendItem => {
        return {
            color: theme.palette[legendItem.color]?.main,
            label: t(I18N_NAMESPACE, legendItem.label),
        }
    })
        .reverse()
}

export const getTrafficLayerStyle = (legendKey, referentialColor, theme, selectedRoad = null, currentYear = null) => {
    const legendConfig = trafficLegend[legendKey]
    const legendStyles = legendConfig.flatMap((legendItem) => {
        const color = theme.palette[legendItem.color].main
        const values = []

        if (legendItem.minValue !== undefined) {
            values.push([">=", ["get", `${legendKey}Count`], legendItem.minValue])
        }
        if (legendItem.maxValue !== undefined) {
            values.push(["<", ["get", `${legendKey}Count`], legendItem.maxValue])
        }

        return [
            ["all", ...values],
            selectLayerColorOpacity(color, 0.5, selectedRoad),
        ]
    })

    const defaultColor = theme.palette[legendConfig[legendConfig.length - 1].color].main
    legendStyles.push(selectLayerColorOpacity(defaultColor, 0.5, selectedRoad))

    return getLineLayerConfig({
        "paint": {
            "line-color": [
                "case",
                ["!=", ["typeof", ["get", `${legendKey}Count`, ["properties"]]], "number"],
                selectLayerColorOpacity(referentialColor, referentialColor === "#FFFFFF" ? 0.85 : 0.5, selectedRoad),
            ].concat(legendStyles),
            "line-width": getInterpolatedSelectableLineLayerWidth(selectedRoad),
        },
        "layout": {
            "line-sort-key": getSelectableLineSortKey(selectedRoad),
        },
        "filter": ["all",
            ["all",
                ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], "string"],
                ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad]],
            ["==", ["get", "year", ["properties"]], parseInt(currentYear)],
        ],
    })
}

export const getRoadTrafficLayerStyle = (legendKey, referentialColor, theme, selectedRoad = null) => {
    const legendConfig = trafficLegend[legendKey]

    const legendStyles = legendConfig.flatMap((legendItem) => {
        const color = theme.palette[legendItem.color].main
        const values = []

        if (legendItem.minValue !== undefined) {
            values.push([">=", ["get", legendKey], legendItem.minValue])
        }
        if (legendItem.maxValue !== undefined) {
            values.push(["<", ["get", legendKey], legendItem.maxValue])
        }

        return [
            ["all", ...values],
            color,
        ]
    })
    const defaultColor = theme.palette[legendConfig[legendConfig.length - 1].color].main
    legendStyles.push(selectLayerColorOpacity(defaultColor, 0.5, selectedRoad))

    const getOffset = (offset) => {
        return ["case",
            ["!=", ["typeof", ["get", legendKey, ["properties"]]], "number"],
            0,
            offset,
        ]
    }

    return getLineLayerConfig({
        "paint": {
            "line-color": [
                "case",
                ["!=", ["typeof", ["get", legendKey, ["properties"]]], "number"],
                referentialColor,
            ].concat(legendStyles),
            "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
            "line-offset":
                ["interpolate",
                    ["linear"],
                    ["zoom"],
                    1,
                    getOffset(MIN_LINE_OFFSET),
                    12,
                    getOffset(MIN_LINE_OFFSET),
                    INTERPOLATION_ZOOM_MAX,
                    getOffset(MAX_LINE_OFFSET),
                ],
        },
        "layout": {
            "line-sort-key": getSelectableLineSortKey(selectedRoad),
        },
        "filter": ["all",
            ["==", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad],
        ],
    })
}

